import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { downloadAdminAnalyticsExcel, fetchAdminAnalyticsLatest, fetchCrmActionsToday, fetchCrmSignalExceptions, fetchWatiHandoffs, fetchCrmQualityQueue, generateAdminAnalytics, regenerateAdminAnalytics } from '../api';
import type { AdminAnalyticsReport } from '../types';
import EodExecutiveReport from './EodExecutiveReport';
import EodExecutiveReportPrint from './EodExecutiveReportPrint';
import MorningManagementReport from './MorningManagementReport';
import MorningManagementReportPrint from './MorningManagementReportPrint';
import { sourceLabel } from '../utils/source-label';

type Props = {
  active: boolean;
  isDesktop: boolean;
  onOpenLead: (id: string) => void;
};

// ============================================================================
// DESIGN SYSTEM — Skydash-inspired palette
// ============================================================================
const PALETTE = {
  pageBg: '#F5F7FF',
  indigo: '#4B49AC',
  blue: '#7DA0FA',
  violet: '#7978E9',
  textDark: '#17213A',
  textSecondary: '#8A9AB5',
  border: '#E8EBFB',
  tintLight: '#F8F9FF',
};

function numberValue(value: unknown) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function formatDateTime(value?: string | null) {
  if (!value) return 'Not generated';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).format(new Date(value));
}


function stateLabel(report: AdminAnalyticsReport | null) {
  if (!report) return 'Not generated';
  if (report.status === 'generating') return 'Generating';
  if (report.status === 'completed') return 'Completed';
  if (report.status === 'failed') return 'Failed';
  return 'Not generated';
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

// ============================================================================
// BUTTON PRIMITIVES — visual hierarchy: primary / secondary / tertiary
// ============================================================================

function PrimaryButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg bg-[#4B49AC] px-3.5 py-2 text-[12px] font-semibold text-white transition-all duration-200 hover:bg-[#3d3b8c] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg bg-[#7DA0FA] px-3.5 py-2 text-[12px] font-semibold text-white transition-all duration-200 hover:bg-[#6b8de8] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function TertiaryButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg border border-[#E8EBFB] bg-white px-3.5 py-2 text-[12px] font-medium text-[#17213A] transition-all duration-200 hover:bg-[#F8F9FF] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

// ============================================================================
// REPORT HEADER — executive header with button hierarchy
// ============================================================================

function ReportHeader({ title, subtitle, report, actions }: { title: string; subtitle?: string; report: AdminAnalyticsReport | null; actions: ReactNode }) {
  const dateStr = report?.window_start ? new Date(report.window_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '';
  const timeStart = report?.window_start ? new Date(report.window_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '';
  const timeEnd = report?.window_end ? new Date(report.window_end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '';
  const completed = report?.status === 'completed';
  return (
    <div className="border-b border-[#E8EBFB] bg-white px-6 py-4">
      <div className="flex items-start justify-between gap-4">
        {/* Left: title + date + generated */}
        <div>
          <h2 className="text-[18px] font-bold tracking-tight text-[#17213A]">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[13px] text-[#8A9AB5]">{subtitle}</p>}
          {dateStr && (
            <div className="mt-2 flex items-center gap-3 text-[12px] text-[#8A9AB5]">
              <span className="font-medium text-[#17213A]">{dateStr}</span>
              {timeStart && timeEnd && <span>{timeStart} — {timeEnd} IST</span>}
            </div>
          )}
          {report?.generated_at && (
            <p className="mt-1 text-[12px] text-[#8A9AB5]">Last generated {relativeTime(report.generated_at)}</p>
          )}
        </div>
        {/* Right: actions + status badge */}
        <div className="flex items-center gap-2">
          {actions}
          {completed && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Completed
            </span>
          )}
          {!completed && report && (
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
              report.status === 'generating' ? 'bg-amber-50 text-amber-700' :
              report.status === 'failed' ? 'bg-rose-50 text-rose-700' :
              'bg-slate-100 text-slate-500'
            }`}>
              {stateLabel(report)}
            </span>
          )}
        </div>
      </div>
      {report?.generation_error && <p className="mt-2 text-[12px] text-rose-600">{report.generation_error}</p>}
    </div>
  );
}

function numericTotal(rows: Array<Record<string, unknown>>, key: string) {
  return rows.reduce((sum, row) => sum + numberValue(row[key]), 0);
}

function MorningTable({ report }: { report: AdminAnalyticsReport }) {
  const rows = report.source_summary;
  return (
    <div className="p-6">
      <div className="max-w-md overflow-hidden rounded-xl border border-[#E8EBFB]">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-[#F8F9FF] text-[#8A9AB5]">
            <tr>
              <th className="px-4 py-2.5 text-[12px] font-semibold">Source</th>
              <th className="px-4 py-2.5 text-right text-[12px] font-semibold">New Leads</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.source)} className="border-t border-[#F3F6FF]">
                <td className="px-4 py-2.5 font-medium text-[#17213A]">{sourceLabel(row.source)}</td>
                <td className="px-4 py-2.5 text-right text-[#17213A]">{numberValue(row.new_leads)}</td>
              </tr>
            ))}
            <tr className="border-t border-[#E8EBFB] bg-[#F8F9FF] font-semibold text-[#17213A]">
              <td className="px-4 py-2.5">Total</td>
              <td className="px-4 py-2.5 text-right">{numericTotal(rows, 'new_leads')}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN DASHBOARD
// ============================================================================

export default function AdminAnalyticsDashboard({ active, isDesktop, onOpenLead }: Props) {
  const [eod, setEod] = useState<AdminAnalyticsReport | null>(null);
  const [morning, setMorning] = useState<AdminAnalyticsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionsToday, setActionsToday] = useState<Awaited<ReturnType<typeof fetchCrmActionsToday>> | null>(null);
  const [actionsError, setActionsError] = useState(false);
  const [signalExceptions, setSignalExceptions] = useState<Awaited<ReturnType<typeof fetchCrmSignalExceptions>>['items'] | null>(null);
  const [signalExceptionError, setSignalExceptionError] = useState(false);
  const [handoffs, setHandoffs] = useState<Awaited<ReturnType<typeof fetchWatiHandoffs>>['items'] | null>(null);
  const [handoffError, setHandoffError] = useState(false);
  const [qualityQueue, setQualityQueue] = useState<Awaited<ReturnType<typeof fetchCrmQualityQueue>> | null>(null);
  const [qualityError, setQualityError] = useState(false);
  const showQualityQueue = async (queue: 'attribution' | 'disposition') => {
    setQualityError(false);
    setQualityQueue(null);
    try { setQualityQueue(await fetchCrmQualityQueue(queue)); }
    catch { setQualityError(true); }
  };
  const showHandoffs = async () => {
    setHandoffError(false);
    setHandoffs(null);
    try { setHandoffs((await fetchWatiHandoffs()).items); }
    catch { setHandoffError(true); }
  };

  const showSignalExceptions = async () => {
    setSignalExceptionError(false);
    try { setSignalExceptions((await fetchCrmSignalExceptions()).items); }
    catch { setSignalExceptionError(true); }
  };

  const loadActions = async () => {
    setActionsError(false);
    try { setActionsToday(await fetchCrmActionsToday()); }
    catch { setActionsError(true); }
  };

  useEffect(() => {
    if (active && isDesktop) void loadActions();
  }, [active, isDesktop]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [eodResult, morningResult] = await Promise.all([
        fetchAdminAnalyticsLatest('eod'),
        fetchAdminAnalyticsLatest('morning'),
      ]);
      setEod(eodResult.report);
      setMorning(morningResult.report);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active && isDesktop) void load();
  }, [active, isDesktop]);

  const regenerate = async (type: 'eod' | 'morning') => {
    if (!window.confirm(`Regenerate the ${type.toUpperCase()} report for the current window?`)) return;
    setBusy(`regenerate:${type}`);
    setError(null);
    setNotice(null);
    try {
      const result = await regenerateAdminAnalytics(type);
      if (type === 'eod') setEod(result.report ?? result.previous_completed_report ?? null);
      else setMorning(result.report ?? result.previous_completed_report ?? null);
    } catch (err) {
      const msg = String(err);
      // If no report exists yet (404), fall back to generate instead of showing an error.
      if (msg.includes('report not found') || msg.includes('404')) {
        setNotice(`No existing ${type.toUpperCase()} report found. Generating a new one...`);
        try {
          const genResult = await generateAdminAnalytics(type);
          if (type === 'eod') setEod(genResult.report ?? genResult.previous_completed_report ?? null);
          else setMorning(genResult.report ?? genResult.previous_completed_report ?? null);
        } catch (genErr) {
          setError(String(genErr));
        }
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  };

  const generate = async (type: 'eod' | 'morning') => {
    const before = type === 'eod' ? eod : morning;
    setBusy(`generate:${type}`);
    setError(null);
    setNotice(null);
    try {
      const result = await generateAdminAnalytics(type);
      if (before?.status === 'completed' && result.report?.id === before.id) setNotice('This report has already been generated. Showing the saved report.');
      else if (result.message) setNotice(result.message);
      if (type === 'eod') setEod(result.report ?? result.previous_completed_report ?? null);
      else setMorning(result.report ?? result.previous_completed_report ?? null);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  };

  const downloadExcel = async (report: AdminAnalyticsReport) => {
    setBusy(`download:${report.report_type}`);
    setError(null);
    try {
      await downloadAdminAnalyticsExcel(report);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = (report: AdminAnalyticsReport) => {
    setBusy(`pdf:${report.report_type}`);
    setTimeout(() => {
      window.print();
      setBusy(null);
    }, 100);
  };

  const downloadHtml = (report: AdminAnalyticsReport) => {
    const section = document.querySelector(`[data-admin-report="${report.report_type}"]`);
    if (!section) return;
    const styles = Array.from(document.querySelectorAll('style')).map((style) => style.outerHTML).join('\n');
    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((link) => `<link rel="stylesheet" href="${(link as HTMLLinkElement).href}">`).join('\n');
    const exportScript = `
      <script>
        (() => {
          const setTab = (group, value) => {
            document.querySelectorAll('[data-export-tab="' + group + '"]').forEach((button) => {
              button.setAttribute('aria-selected', button.getAttribute('data-export-value') === value ? 'true' : 'false');
            });
            document.querySelectorAll('[data-export-panel="' + group + '"]').forEach((panel) => {
              panel.hidden = panel.getAttribute('data-export-value') !== value;
            });
          };
          document.querySelectorAll('[data-export-tab]').forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              setTab(button.getAttribute('data-export-tab'), button.getAttribute('data-export-value'));
            });
          });
        })();
      </script>`;
    const html = `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Santaan ${report.report_type.toUpperCase()} Report</title>${links}${styles}<style>body{margin:0;background:#f5f7ff}main{max-width:1600px;margin:0 auto}button{cursor:pointer}</style></head><body><main>${section.outerHTML}</main>${exportScript}</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `santaan-${report.report_type}-report-${report.window_end.slice(0, 10)}.html`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const shareReport = (report: AdminAnalyticsReport) => {
    const url = `${window.location.origin}/?report=${report.id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setNotice('Report link copied to clipboard. Note: this link requires admin login. A public shareable URL requires backend support.');
      }).catch(() => {
        setNotice(`Share link: ${url}`);
      });
    } else {
      setNotice(`Share link: ${url}`);
    }
  };

  const actionsFor = (type: 'eod' | 'morning', report: AdminAnalyticsReport | null) => {
    const isGenerating = report?.status === 'generating' || busy !== null;
    const completed = report?.status === 'completed';
    const hasReport = !!report;

    // EOD: button hierarchy — Primary: Download PDF, Secondary: Share, Tertiary: Regenerate/Excel
    if (type === 'eod') {
      return (
        <>
          {!hasReport && (
            <PrimaryButton onClick={() => generate(type)} disabled={isGenerating}>
              {busy === `generate:${type}` ? 'Generating...' : 'Generate EOD'}
            </PrimaryButton>
          )}
          {hasReport && (
            <TertiaryButton onClick={() => regenerate(type)} disabled={isGenerating}>
              {busy === `regenerate:${type}` ? 'Regenerating...' : 'Regenerate'}
            </TertiaryButton>
          )}
          {completed && (
            <>
              <SecondaryButton onClick={() => report && shareReport(report)} disabled={isGenerating}>
                Share Report
              </SecondaryButton>
              <PrimaryButton onClick={() => report && downloadPdf(report)} disabled={isGenerating}>
                {busy === `pdf:${type}` ? 'Preparing...' : 'Download PDF'}
              </PrimaryButton>
              <TertiaryButton onClick={() => report && downloadHtml(report)} disabled={isGenerating}>
                Download HTML
              </TertiaryButton>
              <TertiaryButton onClick={() => report && downloadExcel(report)} disabled={isGenerating}>
                {busy === `download:${type}` ? 'Downloading...' : 'Download Excel'}
              </TertiaryButton>
            </>
          )}
        </>
      );
    }

    // Morning: Generate / Regenerate / PDF / Excel
    return (
      <>
        {!hasReport && (
          <PrimaryButton onClick={() => generate(type)} disabled={isGenerating}>
            {busy === `generate:${type}` ? 'Generating...' : 'Generate Morning'}
          </PrimaryButton>
        )}
        {hasReport && (
          <TertiaryButton onClick={() => regenerate(type)} disabled={isGenerating}>
            {busy === `regenerate:${type}` ? 'Regenerating...' : 'Regenerate'}
          </TertiaryButton>
        )}
        {completed && (
          <>
            <PrimaryButton onClick={() => report && downloadPdf(report)} disabled={isGenerating}>
              {busy === `pdf:${type}` ? 'Preparing...' : 'Download PDF'}
            </PrimaryButton>
            <TertiaryButton onClick={() => report && downloadHtml(report)} disabled={isGenerating}>
              Download HTML
            </TertiaryButton>
            <TertiaryButton onClick={() => report && downloadExcel(report)} disabled={isGenerating}>
              {busy === `download:${type}` ? 'Downloading...' : 'Download Excel'}
            </TertiaryButton>
          </>
        )}
      </>
    );
  };

  if (!isDesktop) {
    return (
      <div className="p-5 text-sm text-[#8A9AB5]">
        Analytics dashboard is available on desktop.
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F5F7FF]">
      {/* Page header — controlled canvas */}
      <div className="mx-auto max-w-[1600px] px-6 pt-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-[24px] font-bold tracking-tight text-[#17213A]">Admin Reports</h1>
            <p className="mt-0.5 text-[13px] text-[#8A9AB5]">Executive snapshots · shared across all admins</p>
          </div>
        </div>
        {error && <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{error}</div>}
        {notice && <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">{notice}</div>}
      </div>

      {/* Report sections — controlled canvas */}
      <div className="mx-auto max-w-[1600px] px-6 pb-6 pt-5">
        <section className="mb-5 rounded-2xl border border-[#E8EBFB] bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-[#17213A]">What needs attention today</h2>
            <button onClick={() => void loadActions()} className="text-xs text-[#4B49AC]">Refresh counts</button>
          </div>
          {actionsError ? <p className="mt-2 text-xs text-rose-700">Current counts could not be loaded. Refresh to retry.</p> : actionsToday ? <>
            <p className="mt-1 text-xs text-slate-500">Live work backlog · checked {formatDateTime(actionsToday.as_of)} · counts may overlap</p>
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
              {actionsToday.items.map((item) => <div key={item.id} className="rounded-lg bg-[#F8F9FF] p-3">
                <div className="text-lg font-semibold text-[#17213A]">{item.count ?? 'Unavailable'}</div>
                <div className="text-xs font-medium text-slate-700">{item.label}</div>
                <div className="mt-1 text-xs text-slate-500">{item.action}</div>
                {item.id === 'signals' && item.count !== null && <button onClick={() => void showSignalExceptions()} className="mt-2 text-xs font-semibold text-[#4B49AC]">Review exceptions</button>}
                {item.id === 'handoffs' && item.count !== null && <button onClick={() => void showHandoffs()} className="mt-2 text-xs font-semibold text-[#4B49AC]">Review adviser requests</button>}
                {(item.id === 'attribution' || item.id === 'disposition') && item.count !== null && <button onClick={() => void showQualityQueue(item.id as 'attribution' | 'disposition')} className="mt-2 text-xs font-semibold text-[#4B49AC]">Review enquiries</button>}
              </div>)}
            </div>
          </> : <p className="mt-2 text-xs text-slate-500">Loading current work...</p>}
          {signalExceptionError && <p className="mt-2 text-xs text-rose-700">Exceptions could not be loaded. Select Review exceptions to retry.</p>}
          {handoffError && <p className="mt-2 text-xs text-rose-700">Adviser requests could not be loaded. Select Review adviser requests to retry.</p>}
          {qualityError && <p className="mt-2 text-xs text-rose-700">Enquiries could not be loaded. Select Review enquiries to retry.</p>}
          {qualityQueue && <div className="mt-4 border-t pt-3 text-xs">
            <div className="mb-2 flex justify-between font-semibold"><span>{qualityQueue.action} · oldest 50</span><button onClick={() => setQualityQueue(null)}>Close</button></div>
            {!qualityQueue.items.length && <p>No enquiries need this check.</p>}
            {qualityQueue.items.map(item => <div key={item.lead_id} className="flex items-center justify-between gap-3 border-b py-2">
              <div>{item.name || 'Unnamed enquiry'} · received {formatDateTime(item.created_at)}</div>
              <button onClick={() => onOpenLead(item.lead_id)} className="shrink-0 font-semibold text-[#4B49AC]">Open lead</button>
            </div>)}
          </div>}
          {handoffs && <div className="mt-4 border-t pt-3 text-xs">
            <div className="mb-2 flex justify-between font-semibold"><span>Adviser requests · oldest 50</span><button onClick={() => setHandoffs(null)}>Close</button></div>
            {!handoffs.length && <p>No adviser requests waiting for a human reply.</p>}
            {handoffs.map((item) => <div key={item.lead_id} className="flex items-center justify-between gap-3 border-b py-2">
              <div><div className="font-medium">{item.name || 'Unnamed enquiry'} · {item.next_action}</div>
                <div className="mt-1 text-slate-500">{item.assigned_to || 'Unassigned'} · requested {formatDateTime(item.requested_at)}</div></div>
              <button onClick={() => onOpenLead(item.lead_id)} className="shrink-0 font-semibold text-[#4B49AC]">Open lead</button>
            </div>)}
          </div>}
          {signalExceptions && <div className="mt-4 border-t pt-3 text-xs">
            <div className="mb-2 flex justify-between font-semibold"><span>Provider exceptions · oldest 50 · technical review needed</span><button onClick={() => setSignalExceptions(null)}>Close</button></div>
            {!signalExceptions.length && <p>No unresolved provider receipts.</p>}
            {signalExceptions.map((item) => <div key={`${item.provider}:${item.delivery_id}`} className="border-b py-2">
              <span className="font-medium">{item.provider} · {item.event_type}</span> · {item.reason || item.state} · {item.attempts} attempts
              <div className="mt-1 break-all text-slate-500">{item.delivery_id} · received {item.received_at}</div>
            </div>)}
          </div>}
        </section>
        {loading ? (
          <div className="p-5 text-sm text-[#8A9AB5]">Loading saved reports...</div>
        ) : (
          <div className="space-y-6">
            <section data-admin-report="eod" className="overflow-hidden rounded-2xl border border-[#E8EBFB] bg-white shadow-[0_4px_20px_rgba(75,73,172,0.05)]">
              <ReportHeader title="EOD Performance" subtitle="Daily Growth & Calling Report" report={eod} actions={actionsFor('eod', eod)} />
              {eod?.status === 'completed'
                ? <EodExecutiveReport report={eod} />
                : <div className="p-8 text-center text-sm text-[#8A9AB5]">{eod?.status === 'generating' ? 'Report generation is already in progress.' : 'No completed EOD snapshot found. Click Generate EOD to create one.'}</div>}
            </section>

            <section data-admin-report="morning" className="overflow-hidden rounded-2xl border border-[#E8EBFB] bg-white shadow-[0_4px_20px_rgba(75,73,172,0.05)]">
              <ReportHeader title="Morning Management Report" subtitle="Yesterday's leads · pending actions · marketing quality" report={morning} actions={actionsFor('morning', morning)} />
              {morning?.status === 'completed'
                ? <MorningManagementReport report={morning} onRegenerate={() => void regenerate('morning')} regenerating={busy === 'regenerate:morning'} />
                : <div className="p-8 text-center text-sm text-[#8A9AB5]">{morning?.status === 'generating' ? 'Report generation is already in progress.' : 'No completed morning snapshot found. Click Generate Morning to create one.'}</div>}
            </section>
          </div>
        )}
      </div>

      {/* Print-only EOD report — portaled to document.body by the component itself,
          hidden on screen via .eod-print-report { display: none },
          shown during print via @media print { #root { display: none } .eod-print-report { display: block } } */}
      {eod?.status === 'completed' && <EodExecutiveReportPrint report={eod} />}
      {morning?.status === 'completed' && <MorningManagementReportPrint report={morning} />}
    </div>
  );
}
